import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Courses", path: "/courses" },
  { label: "Contact", id: "inquire" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    const run = () => {
      if (l.path) {
        if (pathname !== l.path) navigate(l.path);
        else window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (isHome) {
        scrollToId(l.id);
      } else {
        navigate(`/#${l.id}`);
      }
    };
    setTimeout(run, open ? 250 : 0);
  };

  const solid = scrolled || !isHome;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          solid
            ? "bg-[rgba(251,246,238,0.88)] backdrop-blur-xl border-b border-[#E9DCC8]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16 sm:h-[72px]">
          <button
            data-testid="nav-brand-logo"
            onClick={() => {
              setOpen(false);
              if (isHome) window.scrollTo({ top: 0, behavior: "smooth" });
              else navigate("/");
            }}
            className="font-serif-ed text-lg sm:text-xl tracking-[0.35em] text-[#2A1F16] hover:text-[#96754D] transition-colors duration-300"
          >
            MRINALEE
          </button>

          <div className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.id || "courses"}`}
                onClick={() => go(l)}
                className={`text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  l.path && pathname === l.path
                    ? "text-[#B8893B]"
                    : "text-[#6B5A49] hover:text-[#2A1F16]"
                }`}
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-enquire-button"
              onClick={() => go({ id: "inquire" })}
              className="text-[11px] uppercase tracking-[0.22em] bg-[#C9A25F] text-[#2A1F16] font-medium px-6 py-3 rounded-full hover:bg-[#2A1F16] hover:text-[#FBF6EE] transition-colors duration-300"
            >
              Book Your Date
            </button>
          </div>

          <button
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-[#2A1F16]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#FBF6EE]/97 backdrop-blur-2xl flex flex-col items-center justify-center gap-8 md:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.label}
                data-testid={`nav-mobile-link-${l.id || "courses"}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.07 * i, duration: 0.5 }}
                onClick={() => go(l)}
                className="font-serif-ed text-3xl text-[#2A1F16]"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              data-testid="nav-mobile-enquire-button"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              onClick={() => go({ id: "inquire" })}
              className="mt-4 text-[11px] uppercase tracking-[0.25em] bg-[#C9A25F] text-[#2A1F16] font-medium px-8 py-4 rounded-full"
            >
              Book Your Date
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
